import React, { useEffect, useState } from 'react';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { getApiUrl } from '../../config/api';

const initialReview = {
  authorName: '',
  rating: 5,
  comment: '',
  reviewDate: '',
  profilePhoto: '',
  isActive: true
};

const GoogleReviewsAdmin = () => {
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [review, setReview] = useState(initialReview);
  const [placeId, setPlaceId] = useState('');
  const [importing, setImporting] = useState(false);

  const fetchItems = () => {
    fetch(getApiUrl('/api/admin/google-reviews'), { headers: { 'x-admin': 'true' } })
      .then(res => res.json())
      .then(data => setItems(Array.isArray(data) ? data : []));
  };

  useEffect(() => { fetchItems(); }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setReview(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const openCreate = () => {
    setEditing(null);
    setReview(initialReview);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setReview({
      authorName: item.authorName || '',
      rating: item.rating || 5,
      comment: item.comment || '',
      reviewDate: item.reviewDate ? item.reviewDate.slice(0, 10) : '',
      profilePhoto: item.profilePhoto || '',
      isActive: item.isActive !== false
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...review,
      rating: Number(review.rating),
      reviewDate: review.reviewDate ? new Date(review.reviewDate) : new Date()
    };
    const url = editing
      ? getApiUrl(`/api/admin/google-reviews/${editing._id}`)
      : getApiUrl('/api/admin/google-reviews');
    const res = await fetch(url, {
      method: editing ? 'PUT' : 'POST',
      headers: { 'x-admin': 'true', 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      alert('Failed to save Google review');
      return;
    }
    setShowModal(false);
    fetchItems();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this Google review?')) return;
    await fetch(getApiUrl(`/api/admin/google-reviews/${id}`), {
      method: 'DELETE',
      headers: { 'x-admin': 'true' }
    });
    fetchItems();
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await fetch(getApiUrl('/api/admin/google-reviews/import'), {
        method: 'POST',
        headers: { 'x-admin': 'true', 'Content-Type': 'application/json' },
        body: JSON.stringify({ placeId })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.message || 'Could not import from Google. Add reviews manually below, or set GOOGLE_PLACES_API_KEY in Vercel.');
        return;
      }
      alert(`Imported ${data.imported} Google review(s).`);
      fetchItems();
    } catch {
      alert('Import failed. You can still add Google reviews manually.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Google Reviews</h1>
        <button onClick={openCreate} className="bg-pink-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-pink-700">
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Google Review
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <p className="text-sm text-gray-600 mb-3">
          Copy reviews from your Google Business Profile, or import automatically if a Place ID and Google Places API key are set.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={placeId}
            onChange={(e) => setPlaceId(e.target.value)}
            placeholder="Google Place ID (optional)"
            className="flex-1 rounded-md border-gray-300"
          />
          <button
            onClick={handleImport}
            disabled={importing}
            className="bg-gray-800 text-white px-4 py-2 rounded-lg disabled:opacity-60"
          >
            {importing ? 'Importing…' : 'Import from Google'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Review</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.map((row) => (
              <tr key={row._id}>
                <td className="px-6 py-4 font-medium">{row.authorName}</td>
                <td className="px-6 py-4">{row.rating}/5</td>
                <td className="px-6 py-4 text-sm text-gray-600 max-w-md truncate">{row.comment}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 text-xs font-semibold rounded-full ${row.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {row.isActive ? 'Visible' : 'Hidden'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button className="text-pink-600 mr-2" onClick={() => openEdit(row)}><PencilIcon className="w-5 h-5" /></button>
                  <button className="text-red-500" onClick={() => handleDelete(row._id)}><TrashIcon className="w-5 h-5" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                  No Google reviews yet. Add them here to show on the website.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">{editing ? 'Edit Google Review' : 'Add Google Review'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Customer name</label>
                <input name="authorName" value={review.authorName} onChange={handleChange} required className="mt-1 block w-full rounded-md border-gray-300" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Rating</label>
                <select name="rating" value={review.rating} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300">
                  {[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{n} star{n > 1 ? 's' : ''}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Review text</label>
                <textarea name="comment" value={review.comment} onChange={handleChange} required rows={4} className="mt-1 block w-full rounded-md border-gray-300" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Date</label>
                <input type="date" name="reviewDate" value={review.reviewDate} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300" />
              </div>
              <div className="flex items-center">
                <input type="checkbox" name="isActive" checked={review.isActive} onChange={handleChange} className="h-4 w-4 text-pink-600 rounded" />
                <label className="ml-2 text-sm">Show on website</label>
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-md">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-pink-600 text-white rounded-md">{editing ? 'Update' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleReviewsAdmin;
